// Unpublished Work © 2023-2024 Deere & Company.

import { Box, Card, CardContent, SxProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Address } from "../../api/invoices/interfaces";
import NoDataAvailableError from "../../pages/orders/components/NoDataAvailableError/NoDataAvailableError";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

const ShippingInformation = ({
  shippingAddress,
  showLoader,
  type,
  carrierDetails,
  pageName = "order",
  sx,
}: {
  shippingAddress: Address | undefined;
  showLoader: boolean;
  type: string;
  carrierDetails: string;
  pageName?: string;
  sx?: SxProps;
}) => {
  const theme = useTheme();
  const { t } = useTranslation("invoices");
  const identifier = `${type.replace(/\s/g, "")}-${pageName}`;

  return (
    <Card sx={sx}>
      <CardContent>
        <Typography
          sx={{
            fontSize: theme.tokens.FuelFontSizeBody,
            fontWeight: "600",
            color: theme.tokens.FuelColorTextPrimary,
          }}
          data-testid={`${identifier}-addressType`}
        >
          {type}
        </Typography>

        {showLoader && <LoadingIndicator />}

        {!showLoader &&
          (shippingAddress ? (
            <Box paddingTop={theme.tokens.FuelSpacing4}>
              <Typography data-testid={`${identifier}-addressLine1`}>
                {shippingAddress.addressLine1}
              </Typography>

              <Typography data-testid={`${identifier}-addressLine2`}>
                {shippingAddress.addressLine2}
              </Typography>

              <Typography data-testid={`${identifier}-addressLine3`}>
                {shippingAddress.cityName}
                {shippingAddress.stateOrProvince ? ", " : ""}
                {shippingAddress.stateOrProvince}{" "}
                {shippingAddress.postalOrZipCode}
              </Typography>

              <Typography
                data-testid={`${identifier}-address-countryCode-phoneNumber`}
              >
                {shippingAddress.countryCode}
                {shippingAddress.phoneNumber ? "-" : ""}
                {shippingAddress.phoneNumber}
              </Typography>
              <br />
              <Typography color="tertiary">
                {t("invoiceDetails.label.carrier")}
              </Typography>
              <Typography data-testid={`${identifier}-carrierDetails`}>
                {carrierDetails}
              </Typography>
              <br />
              <Typography color="tertiary">
                {t("invoiceDetails.label.weight")}
              </Typography>
              <Typography>---</Typography>
            </Box>
          ) : (
            <NoDataAvailableError />
          ))}
      </CardContent>
    </Card>
  );
};

export default ShippingInformation;
